import { FC, useMemo } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { FormControl } from '@mui/material';
import { EAssetType } from 'constants/enums';

import Input from 'components/Input/Input';
import Switch from 'components/Switch/Switch';

import Button from '../../../Button/Button';
import InputSelectDropdown from '../../../InputSelectDropdown/InputSelectDropdown';
import { cn } from '../../../lib/utils';
import Typography from '../../../Typography/Typography';
import { FormField, FormItem, UIInput } from '../../../ui';
import { MEDIA_TYPE_OPTIONS } from '../constants';

import { UploadContentProps } from './types';
import { useUploadContent } from './useUploadContent';

const UploadContent: FC<UploadContentProps> = ({
  control,
  watch,
  setValue,
  errors,
  forceType
}) => {
  const {
    isDragging,
    handleInputChange,
    handleDragOver,
    handleDragLeave,
    onDrop,
    handleButtonClick,
    fileSrcRef,
    inputRef
  } = useUploadContent(setValue);

  const acceptTypes = useMemo(() => {
    if (
      forceType === EAssetType.LOGO ||
      forceType === EAssetType.RECEIPT_LOGO ||
      forceType === EAssetType.RECEIPT_HEADER
    ) {
      return 'image/png, image/jpeg, image/webp';
    }
    return 'image/png, image/jpeg, image/svg+xml, image/gif, image/webp, application/json';
  }, [forceType]);

  const imageRef = useMemo(() => {
    if (watch('isExtrenalImageUrl')) {
      return watch('externalImageUrl');
    }
    if (fileSrcRef.current) {
      return fileSrcRef.current;
    }
    return null;
  }, [
    watch('externalImageUrl'),
    watch('isExtrenalImageUrl'),
    fileSrcRef.current
  ]);

  return (
    <div className="flex flex-col gap-4 max-h-[600px]">
      <div
        data-testid="UploadContent-DropZone"
        onDragOver={(e) => handleDragOver(e)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => onDrop(e)}
        className={cn(
          'relative w-full h-[300px] flex flex-col items-center justify-center bg-base-accent rounded-md border-dashed border border-indigo-200 overflow-hidden',
          fileSrcRef?.current &&
            'bg-contain bg-center bg-no-repeat border-transparent',
          isDragging && 'border-indigo-600 border-solid'
        )}
        style={{
          backgroundImage: imageRef ? `url(${imageRef})` : '',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center'
        }}
      >
        {imageRef ? (
          <div
            data-testid="UploadContent-ReplaceImage"
            onClick={(e) => handleButtonClick(e)}
            className={cn(
              'group absolute inset-0 bg-image-upload-hover hover:bg-image-replace-hover flex items-center justify-center hover:cursor-pointer',
              isDragging && 'bg-image-replace-drag'
            )}
          >
            <Typography
              tag="button"
              className={cn(
                'hidden group-hover:flex gap-x-1 items-center text-p-s text-base-white group-hover:text-base-white',
                isDragging && 'flex'
              )}
            >
              <FontAwesomeIcon icon="fa-regular fa-arrows-rotate-reverse" />
              <span className="group-hover:underline">Replace Image</span>
            </Typography>
          </div>
        ) : (
          <>
            <Typography className="text-p-xl text-indigo-600 mb-0.5">
              Start adding your files
            </Typography>
            <Typography className="text-p-s text-base-mutedForeground">
              Drag and drop files or upload from your computer
            </Typography>
            <Button
              type="button"
              data-testid="UploadContent-UploadButton"
              disabled={watch('isExtrenalImageUrl')}
              className="mt-6"
              isUploadButton
              rightIcon={<FontAwesomeIcon icon="fa-solid fa-plus" />}
              onClick={(e) => handleButtonClick(e)}
            >
              Upload
            </Button>
          </>
        )}
        <div className="hidden">
          <FormField
            name="image"
            control={control}
            render={() => (
              <FormItem>
                <FormControl>
                  <UIInput
                    type="file"
                    data-testid="UploadContent-ImageInput"
                    accept={acceptTypes}
                    ref={inputRef}
                    onChange={(e) => handleInputChange(e)}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
      <div className="w-full flex flex-col gap-y-4">
        <div className="flex gap-x-4 items-center">
          <div className="w-[75%]">
            <FormField
              name="externalImageUrl"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <Input
                    disabled={!watch('isExtrenalImageUrl')}
                    placeholder="https://"
                    data-testid="UploadContent-ImageURLInput"
                    value={value}
                    onChange={onChange}
                    isRequired={true}
                    state={errors.mediaId?.message ? 'error' : 'default'}
                    description={errors.mediaId?.message}
                  />
                </FormItem>
              )}
            />
          </div>
          <div className="flex items-center">
            <FormField
              name="isExtrenalImageUrl"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Switch
                  label="Image URL"
                  data-testid="UploadContent-ImageURL"
                  textAlignment="right"
                  name="isExtrenalImageUrl"
                  defaultChecked={value}
                  onCheckedChange={(checked) =>
                    onChange(!!checked, { shouldValidate: true })
                  }
                />
              )}
            />
          </div>
        </div>
        <FormField
          name="mediaId"
          control={control}
          render={({ field: { value, onChange } }) => (
            <FormItem>
              <Input
                placeholder="* ID Number"
                data-testid="UploadContent-ImageIDInput"
                value={value}
                onChange={onChange}
                isRequired={true}
                state={errors.mediaId?.message ? 'error' : 'default'}
                description={errors.mediaId?.message}
              />
            </FormItem>
          )}
        />
        <FormField
          name="type"
          control={control}
          render={({ field: { onChange } }) => (
            <FormItem>
              <InputSelectDropdown
                buttonDataTestId="UploadContent-TypeSelect"
                listDataTestId="UploadContent-TypeSelectList"
                disabled={!!forceType}
                defaultSelected={MEDIA_TYPE_OPTIONS.find(
                  (option) => option.value === watch('type')
                )}
                options={MEDIA_TYPE_OPTIONS}
                onOptionChange={onChange}
              />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

export default UploadContent;
